import { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faFileExport } from "@fortawesome/free-solid-svg-icons";
import type {
  TeacherCohortProgressMatrixAssignment,
  TeacherCohortProgressMatrixCell,
  TeacherCohortProgressMatrixStudent,
} from "../auth/api-client";
import {
  ASSIGNMENT_PROGRESS_EMOJI,
  ASSIGNMENT_PROGRESS_LABELS,
  type AssignmentProgressStatus,
} from "../student/assignmentProgressStatus";
import { teacherSubmissionReviewUrl } from "./submissions/studentProgressState";
import { countPlanItems, formatPlanItemCounts } from "./submissions/cohortProgressCounts";
import { formatMatrixCellDisplay, matrixCellState } from "./submissions/formatMatrixCell";
import {
  exportProgressMatrixGridCsv,
  exportProgressMatrixRowsCsv,
} from "./submissions/exportProgressMatrixCsv";
import { FloatingActionsMenu } from "./FloatingActionsMenu";

const ALL_STATES = "all";

const STATE_FILTER_OPTIONS: { value: AssignmentProgressStatus; label: string }[] = [
  { value: "not_started", label: `${ASSIGNMENT_PROGRESS_EMOJI.not_started} Not started` },
  { value: "in_progress", label: `${ASSIGNMENT_PROGRESS_EMOJI.in_progress} In progress` },
  { value: "needs_help", label: `${ASSIGNMENT_PROGRESS_EMOJI.needs_help} Needs help` },
  { value: "complete", label: `${ASSIGNMENT_PROGRESS_EMOJI.complete} Complete` },
];

const LEGEND_STATUSES: AssignmentProgressStatus[] = [
  "not_started",
  "in_progress",
  "needs_help",
  "complete",
];

function emailName(email: string): string {
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function cellModifier(state: AssignmentProgressStatus): string {
  return state.replaceAll("_", "-");
}

export interface CohortProgressMatrixProps {
  readonly cohortId: string;
  readonly assignments: readonly TeacherCohortProgressMatrixAssignment[];
  readonly students: readonly TeacherCohortProgressMatrixStudent[];
  readonly cells: readonly TeacherCohortProgressMatrixCell[];
}

export function CohortProgressMatrix({ cohortId, assignments, students, cells }: CohortProgressMatrixProps) {
  const [studentQuery, setStudentQuery] = useState("");
  const [assignmentQuery, setAssignmentQuery] = useState("");
  const [stateFilter, setStateFilter] = useState<AssignmentProgressStatus | typeof ALL_STATES>(ALL_STATES);
  const [onlyWithActivity, setOnlyWithActivity] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLButtonElement>(null);

  const cellMap = useMemo(() => {
    const map = new Map<string, TeacherCohortProgressMatrixCell>();
    for (const c of cells) {
      map.set(`${c.studentUserId}:${c.assignmentId}`, c);
    }
    return map;
  }, [cells]);

  const visibleAssignments = useMemo(() => {
    const q = assignmentQuery.trim().toLowerCase();
    if (!q) return assignments;
    return assignments.filter((a) => a.lessonTitle.toLowerCase().includes(q));
  }, [assignments, assignmentQuery]);

  const visibleStudents = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    return students.filter((s) => {
      if (q && !s.email.toLowerCase().includes(q) && !emailName(s.email).toLowerCase().includes(q)) {
        return false;
      }

      const states = visibleAssignments.map((a) =>
        matrixCellState(cellMap.get(`${s.studentUserId}:${a.assignmentId}`))
      );

      if (onlyWithActivity && !states.some((state) => state !== "not_started")) {
        return false;
      }

      if (stateFilter !== ALL_STATES && !states.some((state) => state === stateFilter)) {
        return false;
      }

      return true;
    });
  }, [students, studentQuery, visibleAssignments, cellMap, onlyWithActivity, stateFilter]);

  const assignmentCounts = useMemo(() => countPlanItems(visibleAssignments), [visibleAssignments]);

  const filtersActive =
    onlyWithActivity ||
    stateFilter !== ALL_STATES ||
    studentQuery.trim() !== "" ||
    assignmentQuery.trim() !== "";

  function shouldShowCell(state: AssignmentProgressStatus): boolean {
    if (stateFilter !== ALL_STATES && state !== stateFilter) return false;
    return true;
  }

  function cellIsClickable(assignment: TeacherCohortProgressMatrixAssignment): boolean {
    if (assignment.released === false) return false;
    if (assignment.itemKind === "quiz") return Boolean(assignment.quizId);
    return Boolean(assignment.lessonId);
  }

  function openReview(assignment: TeacherCohortProgressMatrixAssignment, studentUserId: string) {
    if (assignment.itemKind === "quiz" && assignment.quizId) {
      const href = `/teacher/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(assignment.quizId)}?student=${encodeURIComponent(studentUserId)}`;
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (!assignment.lessonId) return;
    const href = teacherSubmissionReviewUrl(
      cohortId,
      assignment.lessonId,
      assignment.lessonTitle,
      studentUserId
    );
    window.open(href, "_blank", "noopener,noreferrer");
  }

  function exportGrid() {
    exportProgressMatrixGridCsv({
      cohortId,
      assignments: visibleAssignments,
      students: visibleStudents,
      cells,
    });
  }

  function exportRows() {
    exportProgressMatrixRowsCsv({
      cohortId,
      assignments: visibleAssignments,
      students: visibleStudents,
      cells,
    });
  }

  if (assignments.length === 0) {
    return (
      <p className="muted teacher-pad">
        No assignments on the cohort plan yet. Add lessons and quizzes from the plan above to build your gradebook here.
      </p>
    );
  }

  if (students.length === 0) {
    return <p className="muted teacher-pad">No active students enrolled in this cohort.</p>;
  }

  return (
    <div className="cohort-progress-matrix">
      <div className="cohort-progress-matrix-toolbar">
        <label className="field cohort-progress-matrix-filter">
          <span className="field-label">Find student</span>
          <input
            className="input"
            type="search"
            value={studentQuery}
            onChange={(ev) => setStudentQuery(ev.target.value)}
            placeholder="Name or email"
          />
        </label>
        <label className="field cohort-progress-matrix-filter">
          <span className="field-label">Find assignment</span>
          <input
            className="input"
            type="search"
            value={assignmentQuery}
            onChange={(ev) => setAssignmentQuery(ev.target.value)}
            placeholder="Lesson or quiz title"
          />
        </label>
        <label className="field cohort-progress-matrix-filter">
          <span className="field-label">Status</span>
          <select
            className="input"
            value={stateFilter}
            onChange={(ev) => setStateFilter(ev.target.value as AssignmentProgressStatus | typeof ALL_STATES)}
          >
            <option value={ALL_STATES}>Any status</option>
            {STATE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="cohort-progress-matrix-toggle">
          <input
            type="checkbox"
            checked={onlyWithActivity}
            onChange={(ev) => setOnlyWithActivity(ev.target.checked)}
          />
          Only students with activity
        </label>
        <div className="teacher-actions-dropdown cohort-progress-matrix-export">
          <button
            ref={exportMenuRef}
            type="button"
            className="btn secondary tight"
            aria-haspopup="menu"
            aria-expanded={exportMenuOpen}
            onClick={() => setExportMenuOpen((open) => !open)}
          >
            Export <FontAwesomeIcon icon={faCaretDown} />
          </button>
          <FloatingActionsMenu
            anchorRef={exportMenuRef}
            open={exportMenuOpen}
            onClose={() => setExportMenuOpen(false)}
          >
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              onClick={() => {
                setExportMenuOpen(false);
                exportGrid();
              }}
            >
              <FontAwesomeIcon icon={faFileExport} className="teacher-actions-menu-icon" />
              As student grid (CSV)
            </button>
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              onClick={() => {
                setExportMenuOpen(false);
                exportRows();
              }}
            >
              <FontAwesomeIcon icon={faFileExport} className="teacher-actions-menu-icon" />
              As rows (CSV)
            </button>
          </FloatingActionsMenu>
        </div>
      </div>

      <p className="muted small cohort-progress-matrix-hint">
        {visibleStudents.length} student{visibleStudents.length === 1 ? "" : "s"} ·{" "}
        {formatPlanItemCounts(assignmentCounts)}
        {filtersActive ? " · filters on" : ""}. Cohort plan in order. Click a cell to review when the assignment is
        released.
      </p>

      <div className="cohort-progress-matrix-legend" aria-hidden>
        {LEGEND_STATUSES.map((status) => (
          <span
            key={status}
            className={`cohort-progress-matrix-legend-item cohort-progress-matrix-cell--${cellModifier(status)}`}
          >
            {ASSIGNMENT_PROGRESS_EMOJI[status]} {ASSIGNMENT_PROGRESS_LABELS[status]}
          </span>
        ))}
      </div>

      <div className="cohort-progress-matrix-scroll" role="region" aria-label="Student progress by assignment" tabIndex={0}>
        <table className="cohort-progress-matrix-table">
          <thead>
            <tr>
              <th className="cohort-progress-matrix-sticky-col cohort-progress-matrix-corner">Student</th>
              {visibleAssignments.map((a) => (
                <th key={a.assignmentId} className="cohort-progress-matrix-lesson-head" title={a.lessonTitle}>
                  <span className="cohort-progress-matrix-lesson-title">{a.lessonTitle}</span>
                  {a.itemKind === "quiz" ? (
                    <span className="cohort-progress-matrix-lesson-closed">Quiz</span>
                  ) : null}
                  {a.released === false ? (
                    <span className="cohort-progress-matrix-lesson-closed">Planned</span>
                  ) : null}
                  {a.closed ? <span className="cohort-progress-matrix-lesson-closed">Closed</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleStudents.map((s) => (
              <tr key={s.studentUserId}>
                <th className="cohort-progress-matrix-sticky-col cohort-progress-matrix-student" scope="row">
                  <span className="cohort-progress-matrix-student-name" title={s.email}>
                    {emailName(s.email)}
                  </span>
                  <span className="cohort-progress-matrix-student-email muted small">{s.email}</span>
                </th>
                {visibleAssignments.map((a) => {
                  const cell = cellMap.get(`${s.studentUserId}:${a.assignmentId}`);
                  const state = matrixCellState(cell);
                  if (!shouldShowCell(state)) {
                    return (
                      <td key={a.assignmentId} className="cohort-progress-matrix-td cohort-progress-matrix-td--filtered">
                        <span className="cohort-progress-matrix-cell-empty muted" aria-hidden>
                          —
                        </span>
                      </td>
                    );
                  }
                  const display = formatMatrixCellDisplay(a, cell, s.email);
                  const title = `${display.title}${a.released === false ? " · Not assigned yet" : ""}${
                    cellIsClickable(a) ? " · Click to review" : ""
                  }`;
                  const clickable = cellIsClickable(a);
                  const cellBody = (
                    <>
                      <span className="cohort-progress-matrix-cell-emoji" aria-hidden>
                        {ASSIGNMENT_PROGRESS_EMOJI[state]}
                      </span>
                      <span className="cohort-progress-matrix-cell-label">{display.primary}</span>
                    </>
                  );
                  return (
                    <td key={a.assignmentId} className="cohort-progress-matrix-td">
                      {clickable ? (
                        <button
                          type="button"
                          className={`cohort-progress-matrix-cell cohort-progress-matrix-cell--${cellModifier(state)} cohort-progress-matrix-cell--clickable`}
                          title={title}
                          onClick={() => openReview(a, s.studentUserId)}
                        >
                          {cellBody}
                        </button>
                      ) : (
                        <span
                          className={`cohort-progress-matrix-cell cohort-progress-matrix-cell--${cellModifier(state)}`}
                          title={title}
                        >
                          {cellBody}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {visibleStudents.length === 0 ? (
              <tr>
                <td colSpan={visibleAssignments.length + 1} className="muted small cohort-progress-matrix-empty">
                  No students match your filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
